import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  InputBase,
  alpha,
  styled,
  Box,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import bell from "../../assets/svg/navbar/bell-icon.svg";
import bookMark from "../../assets/svg/navbar/BookMark-icon.svg";
import ActiveBookMark from "../../assets/svg/navbar/ActiveBookMark.svg";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import "./NavBar.css";
import SearchModal from "./SearchModal";
import { useDispatch, useSelector } from "react-redux";
import { GetGlobalSearch } from "../../store/search/action";
import { GetElementsSidebar } from "../../store/elements/action";
import debounce from "lodash/debounce";
import { UserHeartBeatApi } from "../../services/heartBeat/HeartBeat";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  width: "100%",
  maxWidth: "440px",
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.black, 0.05),
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, `calc(1em + ${theme.spacing(4)})`),
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "100%",
    },
    "&::placeholder": {
      color: "white",
    },
    color: "white",
  },
}));

const NavBar = ({ sidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state?.search?.search);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bookmarkActive, setBookmarkActive] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [moduleNames, setModuleNames] = useState([]);
  const searchRef = useRef(null);
  const modalRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  useEffect(() => {
    dispatch(GetElementsSidebar())
      .then((response) => {
        const data = response?.payload?.data || [];
        setModuleNames(data); // Store full data
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === "/save-list") {
      setBookmarkActive(true);
    } else {
      setBookmarkActive(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        modalRef.current &&
        !modalRef.current.contains(event.target)
      ) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBookmarkClick = () => {
    if (!bookmarkActive) {
      navigate("/save-list");
    }
  };

  const handleFocus = () => {
    setSearchOpen(true);
  };

  const handleSearchChange = (e) => {
    setSearchParam(e.target.value);
  };

  const debouncedSearch = useRef(
    debounce((search) => {
      const requestBody = {
        ModuleTypeIDs: [],
        SearchParam: search,
      };

      dispatch(GetGlobalSearch(requestBody));
    }, 300), // 300 ms delay
  ).current;

  useEffect(() => {
    if (searchParam !== "") {
      debouncedSearch(searchParam);
    }
  }, [searchParam, debouncedSearch]);

  // Heartbeat effect with visibility change detection
  useEffect(() => {
    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

    if (!isAuthenticated) {
      console.log("User not authenticated - heartbeat not started");
      // Clear any existing interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    console.log("User authenticated - starting heartbeat");
    const activeIntervalTime = 30000; // 30 seconds when tab is active
    const inactiveIntervalTime = 90000; // 90 seconds when tab is inactive

    const callHeartbeatAPI = async () => {
      const isAuth = localStorage.getItem("isAuthenticated") === "true";
      if (!isAuth) {
        return;
      }

      try {
        await UserHeartBeatApi({});
        console.log("Heartbeat API called successfully");
      } catch (error) {
        console.error("Heartbeat API error:", error);
      }
    };

    const startHeartbeat = (interval) => {
      // Clear existing interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      // Call immediately first time
      callHeartbeatAPI();

      // Set up new interval
      heartbeatIntervalRef.current = setInterval(() => {
        callHeartbeatAPI();
      }, interval);
    };

    // Start with active interval when component mounts
    startHeartbeat(activeIntervalTime);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is inactive - use 90 second interval
        console.log("Tab inactive - switching to 90s interval");
        startHeartbeat(inactiveIntervalTime);
      } else {
        // Tab is active - use 30 second interval
        console.log("Tab active - switching to 30s interval");
        startHeartbeat(activeIntervalTime);
      }
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup function
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <AppBar
      position="fixed"
      className="appBar"
      style={{ color: "black", backgroundColor: "#3B82F6" }}
    >
      <Toolbar className="toolbar">
        <Typography variant="h5" style={{ color: "#ffffff" }} className="logo">
          ClykOps
        </Typography>
        <Box className="center">
          <Search
            ref={searchRef}
            className="search"
            onClick={handleFocus}
            tabIndex={0}
          >
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search for Documents, SOPs, Trainings, Tests"
              inputProps={{ "aria-label": "search" }}
              onChange={handleSearchChange}
            />
          </Search>
        </Box>
       
      </Toolbar>
      {searchOpen && (
        <SearchModal
          ref={modalRef}
          onClose={() => setSearchOpen(false)}
          moduleNames={moduleNames}
          combinedData={searchResults?.data || {}} // Pass full data here
          searchParam={searchParam}
        />
      )}
    </AppBar>
  );
};

export default NavBar;
